import { useId, type ReactNode } from 'react';

import { cn } from './cn.js';
import { Icon } from './Icon.js';
import { useDialogBehavior } from './useDialogBehavior.js';

export interface ModalProps {
  /** Heading text. Also names the dialog for assistive tech, so it is required. */
  title: string;
  onClose: () => void;
  /**
   * Whether Escape and the backdrop close the dialog. Defaults to true.
   *
   * Set it false only while an action is genuinely in flight. A dialog you cannot leave is a trap,
   * so whatever sets this has to be certain the flight always ends — a fixed delay does, a network
   * request needs its own timeout or abort before it may claim as much.
   */
  dismissible?: boolean;
  /**
   * Changing this re-places focus inside the panel. Pass a value that moves with the content.
   *
   * A dialog that swaps its own content — a form becoming a confirmation, or every control turning
   * disabled while something runs — leaves focus on an element that is gone or inert, and the
   * browser drops it to the body. Somewhere outside the panel is the one place focus must not be
   * while a modal is up, so the content has to say when it changed.
   */
  focusKey?: string | number;
  /** Extra classes on the panel — width overrides, mostly. */
  className?: string;
  /** Shown before the title, e.g. a warning mark. Decorative: the title says what it means. */
  icon?: ReactNode;
  /**
   * When given, a ✕ button in the corner closes the dialog, with this as its label. Translated
   * by the caller, like every string here. Only while `dismissible`.
   */
  closeLabel?: string;
  children: ReactNode;
}

/**
 * A centred dialog over a dimmed page.
 *
 * Lives here rather than in a feature because three screens want one: paying an order, topping up
 * to pay it, and confirming a cancellation. The parts that are easy to get subtly wrong — the
 * focus trap, returning focus to whatever opened it, locking the page behind — are worth writing
 * once and not three times.
 *
 * Focus is genuinely trapped rather than merely moved on open. The notifications slide-over only
 * moves it, which is survivable for something you read; in a dialog holding a PIN field and a Pay
 * button, tabbing out to the page underneath while the overlay still covers it leaves the keyboard
 * somewhere the eye cannot follow.
 *
 * Deliberately not rendered through a portal. The app has a single full-height layout root and no
 * ancestor creates a stacking or clipping context around the content area, so a fixed overlay
 * covers the viewport from where it sits; a portal would add indirection for no behaviour.
 */
export function Modal({
  title,
  onClose,
  dismissible = true,
  focusKey,
  className,
  icon,
  closeLabel,
  children,
}: ModalProps) {
  const titleId = useId();
  const showClose = closeLabel !== undefined && dismissible;
  const { panelRef } = useDialogBehavior<HTMLDivElement>({ dismissible, onClose, focusKey });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      {/*
        A button when it closes the dialog, so the backdrop is one of the focusable things the trap
        cycles through and a screen reader is told what it does; an inert div when it does not, so
        it neither offers a click that goes nowhere nor takes a place in the Tab cycle.
      */}
      {dismissible ? (
        <button
          type="button"
          onClick={onClose}
          aria-label={`${title} — close`}
          className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[1px]"
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" />
      )}

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // -1 keeps it out of the Tab order while still letting focusPanel land here when the panel
        // has no enabled control of its own.
        tabIndex={-1}
        className={cn(
          'relative w-full max-w-md rounded-lg bg-surface p-5 shadow-raised outline-none sm:p-6',
          // Tall content scrolls inside the panel rather than pushing it off a short screen.
          'max-h-[90vh] overflow-y-auto',
          className,
        )}
      >
        <div className={cn('flex items-center gap-3', showClose && 'pr-10')}>
          {icon !== undefined ? <span aria-hidden="true">{icon}</span> : null}
          <h2 id={titleId} className="min-w-0 flex-1 text-lg font-semibold text-ink">
            {title}
          </h2>
        </div>
        <div className="mt-4">{children}</div>
        {/*
          After the content on purpose: the focus trap starts at the first control, which should be
          the dialog's own action, not this. Placed in the corner by position, not by order.
        */}
        {showClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className={cn(
              'absolute top-3 right-3 flex size-11 items-center justify-center rounded-md text-muted sm:top-4 sm:right-4 lg:size-9',
              'hover:bg-surface-alt hover:text-ink',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
            )}
          >
            <Icon name="close" size={20} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
